import { useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

const DEFAULT_INPUT_MIN_WIDTH_PX = 100;

interface Params {
  inputText: string;
  placeholderText?: string;
  labelElement?: HTMLLabelElement | null;
  labelText?: string;
  defaultMinWidth?: number;
  prohibitShrinkingLessThanPlaceholder?: boolean;
}

/**
 * Accepts the text value of a specific input element and calculates a minimum computed width to fit its value.
 */
export const useTextInputMinWidthUpdate = ({
  inputText,
  placeholderText,
  labelElement,
  labelText = placeholderText,
  prohibitShrinkingLessThanPlaceholder,
  defaultMinWidth = DEFAULT_INPUT_MIN_WIDTH_PX,
}: Params) => {
  const [inputElem, setInputElem] = useState<HTMLInputElement | null>(null);
  const [inputMinWidth, setInputMinWidth] = useState(0);
  const [shrinkedLabelWidth, setShrinkedLabelWidth] = useState<number | null>(null);

  const isFirstLabelConfigRef = useRef(true);

  const theme = useTheme();
  const labelShrinkTransitionDuration = theme.transitions.duration.short;
  const isInputTextEmpty = !inputText;

  useEffect(() => {
    if (!labelText || !labelElement) {
      setShrinkedLabelWidth(null);

      return;
    }

    if (isInputTextEmpty) {
      setShrinkedLabelWidth(null);
      isFirstLabelConfigRef.current = false;

      return;
    }

    if (!isFirstLabelConfigRef.current) {
      setShrinkedLabelWidth(measureElementWidth(labelElement, labelText, true));
      return;
    }

    isFirstLabelConfigRef.current = false;

    const timeout = setTimeout(() => {
      const isInputLabelShrinked =
        !labelElement?.classList.contains('MuiInputLabel-root') ||
        labelElement.classList.contains('MuiInputLabel-shrink');

      setShrinkedLabelWidth(isInputLabelShrinked ? measureElementWidth(labelElement, labelText, true) : null);
    }, labelShrinkTransitionDuration);

    return () => {
      clearTimeout(timeout);
    };
  }, [labelElement, labelText, isInputTextEmpty, labelShrinkTransitionDuration]);

  useEffect(() => {
    if (!inputElem) {
      setInputMinWidth(defaultMinWidth);
      return;
    }

    if (prohibitShrinkingLessThanPlaceholder && placeholderText) {
      const inputTextWidth = measureElementWidth(inputElem, inputText || '') ?? defaultMinWidth;
      const placeholderTextWidth = measureElementWidth(inputElem, placeholderText) ?? defaultMinWidth;
      const targetMinTextWidth = shrinkedLabelWidth ?? placeholderTextWidth;

      setInputMinWidth(Math.max(inputTextWidth, targetMinTextWidth));
    } else {
      setInputMinWidth(measureElementWidth(inputElem, inputText || placeholderText || '') ?? defaultMinWidth);
    }
  }, [
    defaultMinWidth,
    inputElem,
    inputText,
    shrinkedLabelWidth,
    placeholderText,
    prohibitShrinkingLessThanPlaceholder,
  ]);

  return { inputMinWidth, inputElem, setInputElem };
};

const measureElementWidth = (element: HTMLElement, text: string, shouldMeasureScale = false) => {
  const ctx = document.createElement('canvas').getContext('2d');
  const widthOffset = 5;

  if (!ctx) {
    return null;
  }

  const computedStyle = window.getComputedStyle(element);

  ctx.font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;
  const textWidth = ctx.measureText(text).width;

  if (!shouldMeasureScale) {
    return textWidth + widthOffset;
  }

  return (measureElementScaledWidth(computedStyle, textWidth) ?? textWidth) + widthOffset;
};

const measureElementScaledWidth = (elemComputedStyle: CSSStyleDeclaration, elemTextWidth: number) => {
  const { transform } = elemComputedStyle;

  if (!transform || transform === 'none') {
    return elemTextWidth;
  }

  const parseTransformMatrixValues = (matchValue: string, expectedLength: number) => {
    const res = matchValue
      .split(',')
      .map((v) => Number.parseFloat(v.trim()))
      .filter((num) => !Number.isNaN(num));

    return res.length === expectedLength ? res : null;
  };

  const parseTransformMatrix = (matrixMatch: RegExpExecArray, matrixType: '2d' | '3d') => {
    const expectedMatrixValuesLength = matrixType === '2d' ? 6 : 16;
    const values = parseTransformMatrixValues(matrixMatch[1], expectedMatrixValuesLength);

    if (!values) {
      return elemTextWidth;
    }

    const [firstValue, secondValue] = values;
    const scaleX = Math.hypot(firstValue, secondValue);

    return elemTextWidth * scaleX;
  };

  let match: RegExpExecArray | null;

  const matrix2dRegex = /^matrix\(([^)]+)\)$/;
  match = matrix2dRegex.exec(transform);

  if (match) {
    return parseTransformMatrix(match, '2d');
  }

  const matrix3dRegex = /^matrix3d\(([^)]+)\)$/;
  match = matrix3dRegex.exec(transform);

  if (match) {
    return parseTransformMatrix(match, '3d');
  }

  return null;
};
